import { useState } from "react";
import { ReviewTileItem, DismissedTile } from "@/types/tile.types";
import { useRemoveTile } from "./useRemoveTile";

export interface UseTileDismissOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
  onTileDismissed?: (dismissedTile: DismissedTile) => void;
  onTileRestored?: (restoredTile: ReviewTileItem) => void;
  confirmBeforeDismiss?: boolean;
  documentId?: string;
}

export const useTileDismiss = (options?: UseTileDismissOptions) => {
  const [isDismissing, setIsDismissing] = useState(false);
  const [dismissingTileId, setDismissingTileId] = useState<string | null>(null);
  const [dismissedTiles, setDismissedTiles] = useState<Map<string, DismissedTile>>(new Map());

  const removeTileMutation = useRemoveTile({
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });

  const dismissSingleTile = async (tile: ReviewTileItem, reason?: string): Promise<boolean> => {
    setDismissingTileId(tile.id);

    try {
      // Call the remove tile API if we have the required data
      const tileId = tile.originalData?.tile_id;

      // Debug logging
      console.log("Debug dismiss:", {
        hasDocumentId: !!options?.documentId,
        documentId: options?.documentId,
        hasTileId: tileId !== undefined,
        tileId: tileId,
        hasQuery: !!tile.query,
        query: tile.query,
        originalDataKeys: tile.originalData ? Object.keys(tile.originalData) : "no originalData",
      });

      if (options?.documentId && tileId !== undefined && tile.query) {
        await removeTileMutation.mutateAsync({
          document_id: [options.documentId],
          query_term: tile.query,
          tile_id: tileId,
        });
      } else {
        // Fallback to mock behavior if API data is missing
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      const dismissedTile: DismissedTile = {
        ...tile,
        dismissedAt: new Date(),
        dismissReason: reason,
      };

      setDismissedTiles((prev) => new Map(prev.set(tile.id, dismissedTile)));

      options?.onTileDismissed?.(dismissedTile);

      return true;
    } catch (error: any) {
      console.error("Error dismissing tile:", error);
      return false;
    } finally {
      setDismissingTileId(null);
    }
  };

  const dismissMultipleTiles = async (
    tiles: ReviewTileItem[],
    reason?: string
  ): Promise<boolean> => {
    if (tiles.length === 0) {
      options?.onError?.("No tiles to dismiss");
      return false;
    }

    setIsDismissing(true);
    let successCount = 0;
    let failedCount = 0;

    try {
      for (const tile of tiles) {
        try {
          await new Promise((resolve) => setTimeout(resolve, 100)); // Small delay between dismissals

          const dismissedTile: DismissedTile = {
            ...tile,
            dismissedAt: new Date(),
            dismissReason: reason,
          };

          setDismissedTiles((prev) => new Map(prev.set(tile.id, dismissedTile)));
          options?.onTileDismissed?.(dismissedTile);
          successCount++;
        } catch (error: any) {
          console.error(`Error dismissing tile ${tile.id}:`, error);
          failedCount++;
        }
      }

      const message =
        failedCount > 0
          ? `${successCount} tiles dismissed, ${failedCount} failed`
          : `${successCount} tiles dismissed successfully`;

      options?.onSuccess?.(message);
      return successCount > 0;
    } catch (error: any) {
      console.error("Error dismissing tiles:", error);
      options?.onError?.(`Failed to dismiss tiles: ${error.message}`);
      return false;
    } finally {
      setIsDismissing(false);
    }
  };

  const dismissByImpact = async (
    tiles: ReviewTileItem[],
    impact: string,
    reason?: string
  ): Promise<boolean> => {
    const tilesToDismiss = tiles.filter((tile) => tile.impact === impact);

    if (tilesToDismiss.length === 0) {
      options?.onError?.(`No tiles found with ${impact} impact`);
      return false;
    }

    const dismissReason = reason || `Bulk dismiss: ${impact} impact items`;
    return await dismissMultipleTiles(tilesToDismiss, dismissReason);
  };

  const dismissByClause = async (
    tiles: ReviewTileItem[],
    clausePattern: string,
    reason?: string
  ): Promise<boolean> => {
    const tilesToDismiss = tiles.filter((tile) =>
      tile.clause?.toLowerCase().includes(clausePattern.toLowerCase())
    );

    if (tilesToDismiss.length === 0) {
      options?.onError?.(`No tiles found matching clause pattern: ${clausePattern}`);
      return false;
    }

    const dismissReason = reason || `Bulk dismiss: clauses matching "${clausePattern}"`;
    return await dismissMultipleTiles(tilesToDismiss, dismissReason);
  };

  const restoreTile = async (tileId: string): Promise<boolean> => {
    const dismissedTile = dismissedTiles.get(tileId);
    if (!dismissedTile) {
      options?.onError?.("Dismissed tile not found");
      return false;
    }

    try {
      // In a real implementation, this would make an API call to restore the tile
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Remove from dismissed tiles
      setDismissedTiles((prev) => {
        const newMap = new Map(prev);
        newMap.delete(tileId);
        return newMap;
      });

      // Create restored tile (remove dismiss metadata)
      const restoredTile: ReviewTileItem = {
        id: dismissedTile.id,
        clause: dismissedTile.clause,
        section: dismissedTile.section,
        reason: dismissedTile.reason,
        analysis: dismissedTile.analysis,
        explanation: dismissedTile.explanation,
        impact: dismissedTile.impact,
      };

      options?.onTileRestored?.(restoredTile);
      options?.onSuccess?.(
        `Tile "${dismissedTile.clause || dismissedTile.id}" restored successfully`
      );

      return true;
    } catch (error: any) {
      console.error("Error restoring tile:", error);
      options?.onError?.(`Failed to restore tile: ${error.message}`);
      return false;
    }
  };

  const restoreAllTiles = async (): Promise<boolean> => {
    const dismissedTilesList = Array.from(dismissedTiles.values());

    if (dismissedTilesList.length === 0) {
      options?.onError?.("No dismissed tiles to restore");
      return false;
    }

    try {
      let successCount = 0;

      for (const dismissedTile of dismissedTilesList) {
        if (await restoreTile(dismissedTile.id)) {
          successCount++;
        }
      }

      options?.onSuccess?.(`${successCount} tiles restored successfully`);
      return successCount > 0;
    } catch (error: any) {
      console.error("Error restoring all tiles:", error);
      options?.onError?.(`Failed to restore tiles: ${error.message}`);
      return false;
    }
  };

  const getDismissedTiles = (): DismissedTile[] => {
    return Array.from(dismissedTiles.values());
  };

  const getDismissedTile = (tileId: string): DismissedTile | undefined => {
    return dismissedTiles.get(tileId);
  };

  const clearDismissedTiles = (): void => {
    setDismissedTiles(new Map());
  };

  const exportDismissedTilesSummary = (): string => {
    const dismissed = Array.from(dismissedTiles.values());

    if (dismissed.length === 0) {
      return "No dismissed tiles";
    }

    const summary = dismissed
      .map((tile, index) => {
        const clause = tile.clause || `Tile ${index + 1}`;
        const reason = tile.dismissReason || "No reason provided";
        const date = tile.dismissedAt.toLocaleString();

        return `${index + 1}. ${clause}\n   Dismissed: ${date}\n   Reason: ${reason}`;
      })
      .join("\n\n");

    return `Dismissed Tiles Summary (${dismissed.length} items)\n${"=".repeat(50)}\n\n${summary}`;
  };

  const undoLastDismiss = async (): Promise<boolean> => {
    const dismissedTilesList = Array.from(dismissedTiles.values());

    if (dismissedTilesList.length === 0) {
      options?.onError?.("No dismissed tiles to undo");
      return false;
    }

    // Find the most recently dismissed tile
    const lastDismissed = dismissedTilesList.reduce((latest, current) =>
      current.dismissedAt > latest.dismissedAt ? current : latest
    );

    return await restoreTile(lastDismissed.id);
  };

  return {
    isDismissing,
    dismissingTileId,
    dismissSingleTile,
    dismissMultipleTiles,
    dismissByImpact,
    dismissByClause,
    restoreTile,
    restoreAllTiles,
    getDismissedTiles,
    getDismissedTile,
    clearDismissedTiles,
    exportDismissedTilesSummary,
    undoLastDismiss,
  };
};
